import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, ArrowLeft, Star, Truck, Shield, Minus, Plus, Zap, Banknote, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);

  useEffect(() => {
    setLoading(true);
    setSelectedImageIdx(0);
    Promise.all([
      axios.get(`${API}/products/${id}`),
      axios.get(`${API}/reviews/product/${id}`).catch(() => ({ data: { reviews: [] } }))
    ])
    .then(([prodRes, revRes]) => { 
      setProduct(prodRes.data.product); 
      setRelated(prodRes.data.related);
      setReviews(revRes.data.reviews || []);
    })
    .catch(() => navigate('/products'))
    .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => {
    if (user) axios.get(`${API}/wishlist`, { withCredentials: true }).then(r => setWishlistIds(r.data.product_ids || [])).catch(() => {});
  }, [user]);

  const addToCart = async () => {
    if (!user) { navigate('/login'); return; }
    try { await axios.post(`${API}/cart/add`, { product_id: product.id, quantity }, { withCredentials: true }); toast.success(`${quantity} item(s) added to cart`); } catch { toast.error('Failed'); }
  };

  const buyNow = () => {
    if (!user) { navigate('/login'); return; }
    navigate('/checkout', { state: { buyNow: { product_id: product.id, quantity, product } } });
  };

  const toggleWishlist = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      const { data } = await axios.post(`${API}/wishlist/toggle`, { product_id: product.id }, { withCredentials: true });
      setWishlistIds(prev => data.action === 'added' ? [...prev, product.id] : prev.filter(i => i !== product.id));
      toast.success(data.message);
    } catch { toast.error('Failed'); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    setReviewLoading(true);
    try {
      const { data } = await axios.post(`${API}/reviews/product/${id}`, { rating: reviewRating, comment: reviewComment }, { withCredentials: true });
      setReviews([data.review, ...reviews]);
      setReviewRating(5);
      setReviewComment('');
      toast.success('Review added successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setReviewLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-cream"><div className="spinner"></div></div>;
  if (!product) return null;
  const isWishlisted = wishlistIds.includes(product.id);

  // Build images array from image_urls or fallback to image_url
  const images = (product.image_urls && product.image_urls.length > 0)
    ? product.image_urls
    : (product.image_url ? [product.image_url] : ['https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=500&h=500&fit=crop']);

  const prevImage = () => setSelectedImageIdx(i => (i > 0 ? i - 1 : images.length - 1));
  const nextImage = () => setSelectedImageIdx(i => (i < images.length - 1 ? i + 1 : 0));

  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) nextImage(); // swipe left → next
      else prevImage();          // swipe right → prev
    }
    setTouchStartX(null);
  };

  return (
    <div className="min-h-screen bg-cream overflow-x-hidden" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-body text-light-brown hover:text-rust mb-6" data-testid="back-btn"><ArrowLeft className="w-4 h-4" /> Back</button>

        <div className="grid md:grid-cols-2 gap-10 mb-16">
          {/* Image Gallery */}
          <div>
            <div
              className="relative rounded-2xl overflow-hidden bg-white border border-border-custom"
              onTouchStart={images.length > 1 ? handleTouchStart : undefined}
              onTouchEnd={images.length > 1 ? handleTouchEnd : undefined}
              style={{ touchAction: images.length > 1 ? 'pan-y' : 'auto', userSelect: 'none' }}
            >
              <img src={images[selectedImageIdx]} alt={product.name} className="w-full aspect-square object-cover transition-opacity duration-300" onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=500&h=500&fit=crop'; }} draggable={false} />
              {product.is_featured && <span className="absolute top-4 left-4 bg-rust text-cream text-sm font-body font-medium px-3 py-1 rounded-full">Featured</span>}
              {product.cod_available && <span className="absolute top-4 right-4 bg-green-600 text-white text-xs font-body font-medium px-3 py-1 rounded-full flex items-center gap-1"><Banknote className="w-3 h-3" /> COD Available</span>}
              {images.length > 1 && (
                <>
                  <button onClick={prevImage} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-md transition-all opacity-0 group-hover:opacity-100 hover:opacity-100 focus:opacity-100" style={{ opacity: 0.7 }} data-testid="prev-image-btn"><ChevronLeft className="w-5 h-5 text-dark-brown" /></button>
                  <button onClick={nextImage} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-md transition-all opacity-0 group-hover:opacity-100 hover:opacity-100 focus:opacity-100" style={{ opacity: 0.7 }} data-testid="next-image-btn"><ChevronRight className="w-5 h-5 text-dark-brown" /></button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
                    <div className="bg-black/50 text-white text-xs font-body px-3 py-1 rounded-full">{selectedImageIdx + 1} / {images.length}</div>
                  </div>
                  {/* Dot indicators */}
                  <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedImageIdx(i)}
                        className={`w-2 h-2 rounded-full transition-all duration-200 ${i === selectedImageIdx ? 'bg-white scale-125' : 'bg-white/50'}`}
                        aria-label={`Go to image ${i + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImageIdx(idx)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all duration-200 ${
                      idx === selectedImageIdx ? 'border-rust shadow-md scale-105' : 'border-transparent opacity-60 hover:opacity-100 hover:border-border-custom'
                    }`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=100'; }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-rust font-medium mb-1">{product.category}</p>
            <h1 className="font-body text-2xl sm:text-3xl md:text-4xl font-bold text-dark-brown mb-2 break-words" data-testid="product-title">{product.name}</h1>
            
            {product.review_count > 0 && (
              <div className="flex items-center gap-2 mb-4">
                 <span className="bg-[#388E3C] text-white text-[12px] font-bold px-2 py-0.5 rounded flex items-center gap-1" style={{fontFamily: 'Roboto, sans-serif'}}>
                    {product.average_rating?.toFixed(1)} 
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                 </span>
                 <span className="text-[#878787] text-[14px] font-medium" style={{fontFamily: 'Roboto, sans-serif'}}>
                   ({product.review_count.toLocaleString()})
                 </span>
              </div>
            )}

            <div className="flex items-baseline gap-3 mb-6 flex-wrap">
              <span className="font-body text-[28px] font-bold text-[#212121]" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', fontFamily: 'Roboto, sans-serif' }} data-testid="product-detail-price">{'\u20B9'}{product.price.toLocaleString('en-IN')}</span>
              {product.mrp && product.mrp > product.price && (
                <>
                  <span className="text-[#878787] text-[16px] line-through" style={{ fontVariantNumeric: 'tabular-nums', fontFamily: 'Roboto, sans-serif' }}>
                    {'\u20B9'}{product.mrp.toLocaleString('en-IN')}
                  </span>
                  <span className="text-[#388E3C] text-[16px] font-bold" style={{fontFamily: 'Roboto, sans-serif'}}>
                    {Math.round(((product.mrp - product.price) / product.mrp) * 100)}% off
                  </span>
                </>
              )}
              {product.wholesale_price && <span className="font-body text-sm text-amber bg-amber/10 px-3 py-1 rounded-full w-full sm:w-max mt-1">Wholesale: {'\u20B9'}{product.wholesale_price}/pc (min {product.min_wholesale_qty})</span>}
            </div>
            <p className="font-body text-base text-light-brown leading-relaxed mb-6 break-words" style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{product.description}</p>

            <div className="flex items-center gap-2 mb-4">
              <div className={`w-2.5 h-2.5 rounded-full ${product.stock > 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="font-body text-sm text-mid-brown">{product.stock > 0 ? `In Stock (${product.stock})` : 'Out of Stock'}</span>
            </div>
            {product.cod_available && (
              <div className="flex items-center gap-2 mb-6 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                <Banknote className="w-4 h-4 text-green-600" />
                <span className="font-body text-sm text-green-700 font-medium">Cash on Delivery available for this product</span>
              </div>
            )}

            <div className="flex items-center gap-4 mb-6">
              <span className="font-body text-sm text-dark-brown font-medium">Quantity:</span>
              <div className="flex items-center border border-border-custom rounded-full overflow-hidden">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-3 py-2 hover:bg-pale" data-testid="qty-decrease"><Minus className="w-4 h-4 text-mid-brown" /></button>
                <span className="px-4 py-2 font-body text-dark-brown font-medium min-w-[48px] text-center" data-testid="qty-display">{quantity}</span>
                <button onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} className="px-3 py-2 hover:bg-pale" data-testid="qty-increase"><Plus className="w-4 h-4 text-mid-brown" /></button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mb-6">
              <Button onClick={addToCart} disabled={product.stock === 0} size="lg" className="flex-1 min-w-[130px] bg-rust hover:bg-rust/90 text-cream font-body rounded-full h-12" data-testid="add-to-cart-btn">
                <ShoppingCart className="w-5 h-5 mr-1 sm:mr-2 shrink-0" /> <span className="whitespace-nowrap text-sm sm:text-base">Add to Cart</span>
              </Button>
              <Button onClick={buyNow} disabled={product.stock === 0} size="lg" className="flex-1 min-w-[130px] bg-dark-brown hover:bg-dark-brown/90 text-cream font-body rounded-full h-12" data-testid="buy-now-btn">
                <Zap className="w-5 h-5 mr-1 sm:mr-2 shrink-0" /> <span className="whitespace-nowrap text-sm sm:text-base">Buy Now</span>
              </Button>
              <Button onClick={toggleWishlist} variant="outline" size="lg"
                className={`rounded-full h-12 px-5 min-w-[48px] justify-center ${isWishlisted ? 'bg-rust/10 border-rust text-rust' : 'border-border-custom text-mid-brown hover:text-rust hover:border-rust'}`} data-testid="wishlist-btn">
                <Heart className={`w-5 h-5 flex-shrink-0 ${isWishlisted ? 'fill-current' : ''}`} />
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              {[{ icon: Truck, label: 'Local Delivery' }, { icon: Shield, label: 'Quality Assured' }, { icon: Star, label: '5-Star Rated' }].map(f => (
                <div key={f.label} className="flex flex-col items-center text-center p-3 bg-white rounded-xl border border-border-custom">
                  <f.icon className="w-5 h-5 text-rust mb-1" /><span className="font-body text-xs text-mid-brown">{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Product Reviews ── */}
        <section className="mb-16">
          <h2 className="font-heading text-2xl font-bold text-dark-brown mb-6">Customer Reviews</h2>
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              {reviews.length === 0 ? (
                <p className="font-body text-light-brown">No reviews yet. Be the first to review this product!</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map(r => (
                    <div key={r.id} className="bg-white p-4 rounded-xl border border-border-custom shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-body font-bold text-dark-brown">{r.user_name}</span>
                        <span className="text-xs text-light-brown">{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < r.rating ? 'fill-rust text-rust' : 'text-gray-300'}`} />
                        ))}
                      </div>
                      {r.comment && <p className="font-body text-sm text-mid-brown mt-2">{r.comment}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="bg-white p-6 rounded-2xl border border-border-custom shadow-sm sticky top-24">
                <h3 className="font-heading text-lg font-bold text-dark-brown mb-4">Write a Review</h3>
                {user ? (
                  <form onSubmit={submitReview}>
                    <div className="mb-4">
                      <label className="block font-body text-sm font-medium text-mid-brown mb-2">Rating</label>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button type="button" key={star} onClick={() => setReviewRating(star)} className="focus:outline-none">
                            <Star className={`w-8 h-8 ${star <= reviewRating ? 'fill-rust text-rust' : 'text-gray-300 hover:text-rust/50'}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block font-body text-sm font-medium text-mid-brown mb-2">Comment (Optional)</label>
                      <textarea
                        value={reviewComment} onChange={(e) => setReviewComment(e.target.value)}
                        className="w-full border border-border-custom rounded-xl p-3 font-body text-sm focus:outline-none focus:border-rust"
                        rows="4" placeholder="Share your experience with this product..."
                      />
                    </div>
                    <Button type="submit" disabled={reviewLoading} className="w-full bg-rust hover:bg-dark-brown text-cream font-body rounded-full h-11">
                      {reviewLoading ? 'Submitting...' : 'Submit Review'}
                    </Button>
                    <p className="text-xs text-light-brown text-center mt-3">You can submit up to 3 reviews per product.</p>
                  </form>
                ) : (
                  <div className="text-center py-6">
                    <p className="font-body text-mid-brown mb-4">Please log in to write a review.</p>
                    <Button onClick={() => navigate('/login')} className="bg-dark-brown text-cream rounded-full">Log In</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section>
            <h2 className="font-heading text-2xl font-bold text-dark-brown mb-6">Related Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {related.map(p => <ProductCard key={p.id} product={p} onAddToCart={(pid) => { if (!user) { navigate('/login'); return; } axios.post(`${API}/cart/add`, { product_id: pid, quantity: 1 }, { withCredentials: true }).then(() => toast.success('Added')).catch(() => toast.error('Failed')); }} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
